/**
 * Auditory scan feedback
 *
 * Tones are synthesized (Web Audio) — no sound assets, works offline. Browsers
 * keep an AudioContext suspended until a user gesture: we self-install one-time
 * unlock listeners AND await resume() before scheduling, so the first scan is
 * already audible. Frequencies are deliberately high-ish — handheld scanner
 * speakers are tiny and barely reproduce low tones.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// Interval between boop ONSETS. Batched boops land at once, so this is what
// makes them countable: 80ms of tone + 80ms of silence per boop. Much tighter
// and a burst reads as a trill, not a count.
var MIN_BOOP_GAP_S = 0.16;
var BOOP_FREQ_HZ = 1046; // C6 — bright enough for tiny handheld speakers
var BOOP_DURATION_S = 0.08;
var ERROR_FREQS_HZ = [440, 294]; // descending, square wave → harmonics carry
var ERROR_TONE_DURATION_S = 0.14;
var ERROR_GAP_S = 0.12; // silence between consecutive buzzes — keeps them countable too
// A lone triangle wave carries far less energy than a square, so the boop gets
// pushed near full scale; the square buzz stays lower and still sounds louder.
var BOOP_GAIN = 0.85;
var ERROR_GAIN = 0.3;
var audioContext = null;
var nextToneTime = 0;
// User-facing mute switch — owned by shared/SoundToggleButton.tsx, which
// persists the preference per user and pushes it in here. Default on.
var soundEnabled = true;
export var setScanSoundEnabled = function (enabled) {
    soundEnabled = enabled;
};
export var isScanSoundEnabled = function () { return soundEnabled; };
var getContext = function () {
    if (typeof window === 'undefined')
        return null;
    var Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor)
        return null;
    if (!audioContext)
        audioContext = new Ctor();
    return audioContext;
};
// Resolves with a RUNNING context, or null if the browser still refuses.
var ensureRunningContext = function () { return __awaiter(void 0, void 0, Promise, function () {
    var context, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                context = getContext();
                if (!context)
                    return [2 /*return*/, null];
                if (!(context.state !== 'running')) return [3 /*break*/, 4];
                _b.label = 1;
            case 1:
                _b.trys.push([1, 3, , 4]);
                return [4 /*yield*/, context.resume()];
            case 2:
                _b.sent();
                return [3 /*break*/, 4];
            case 3:
                _a = _b.sent();
                return [2 /*return*/, null];
            case 4: return [2 /*return*/, context.state === 'running' ? context : null];
        }
    });
}); };
// Autoplay unlock: browsers only allow resume() inside a user gesture. Scans are
// keyboard events, but resume can lag the very first tones — so unlock as early
// as the first touch/keypress anywhere in the app.
var unlockOnFirstGesture = function () {
    if (typeof document === 'undefined')
        return;
    var unlock = function () {
        void ensureRunningContext();
        document.removeEventListener('pointerdown', unlock, true);
        document.removeEventListener('keydown', unlock, true);
    };
    document.addEventListener('pointerdown', unlock, true);
    document.addEventListener('keydown', unlock, true);
};
unlockOnFirstGesture();
var playTone = function (context, startAt, frequency, duration, type, peakGain) {
    var oscillator = context.createOscillator();
    var gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    // fast attack, exponential decay — a click-free blip
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + 0.02);
};
/**
 * One boop per confirmed scan. `count` schedules that many distinct boops on
 * the shared cursor — a flushed batch of N scans sounds like N scans.
 */
export var boopScanRegistered = function (count) {
    if (count === void 0) { count = 1; }
    if (!soundEnabled)
        return;
    void (function () { return __awaiter(void 0, void 0, void 0, function () {
        var context, i, startAt, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, ensureRunningContext()];
                case 1:
                    context = _b.sent();
                    if (!context)
                        return [2 /*return*/];
                    for (i = 0; i < count; i += 1) {
                        startAt = Math.max(context.currentTime, nextToneTime);
                        playTone(context, startAt, BOOP_FREQ_HZ, BOOP_DURATION_S, 'triangle', BOOP_GAIN);
                        nextToneTime = startAt + MIN_BOOP_GAP_S;
                    }
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); })();
};
/**
 * One descending buzz per rejected scan. Shares the cursor with boops so a
 * mixed flush result plays in order (e.g. 8 boops then 2 buzzes).
 */
export var buzzScanError = function (count) {
    if (count === void 0) { count = 1; }
    if (!soundEnabled)
        return;
    void (function () { return __awaiter(void 0, void 0, void 0, function () {
        var context_1, _loop_1, i, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, ensureRunningContext()];
                case 1:
                    context_1 = _b.sent();
                    if (!context_1)
                        return [2 /*return*/];
                    _loop_1 = function (i) {
                        var startAt = Math.max(context_1.currentTime, nextToneTime);
                        ERROR_FREQS_HZ.forEach(function (frequency, index) {
                            playTone(context_1, startAt + index * ERROR_TONE_DURATION_S, frequency, ERROR_TONE_DURATION_S, 'square', ERROR_GAIN);
                        });
                        nextToneTime = startAt + ERROR_FREQS_HZ.length * ERROR_TONE_DURATION_S + ERROR_GAP_S;
                    };
                    for (i = 0; i < count; i += 1) {
                        _loop_1(i);
                    }
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); })();
};
// Manual check from devtools: window.__packingAudioTest() → 8 boops then 2
// buzzes, the sound of a 10-scan burst against an 8-remaining item.
if (typeof window !== 'undefined') {
    ;
    window.__packingAudioTest = function () {
        boopScanRegistered(8);
        buzzScanError(2);
    };
}
